/**
 * Suppliers Service
 *
 * API calls for Suppliers and Supplier Catalog.
 */

import { apiClient } from '@/api/client';
import type {
  Supplier,
  SupplierWithStats,
  CreateSupplierDto,
  UpdateSupplierDto,
  QuerySuppliersDto,
  SupplierKPIs,
  SupplierCatalogItemWithDetails,
  CreateCatalogItemDto,
  UpdateCatalogItemDto,
  UpdateCatalogItemPriceDto,
  QueryCatalogItemsDto,
  PriceHistoryResponse,
  RawProductPurchasePrice,
  // API Response types
  ApiSuccess,
  SuppliersListResponse,
  SupplierDetailResponse,
  SupplierCreateResponse,
  SupplierUpdateResponse,
  SupplierKPIsResponse,
  CatalogItemsListResponse,
  CatalogItemDetailResponse,
  CatalogItemCreateResponse,
  CatalogItemUpdateResponse,
  RawProductPurchasePricesResponse,
} from '@bstock/shared';

// Re-export types for convenience
export type {
  Supplier,
  SupplierWithStats,
  CreateSupplierDto,
  UpdateSupplierDto,
  QuerySuppliersDto,
  SupplierKPIs,
  SupplierCatalogItemWithDetails,
  CreateCatalogItemDto,
  UpdateCatalogItemDto,
  UpdateCatalogItemPriceDto,
  QueryCatalogItemsDto,
  PriceHistoryResponse,
  RawProductPurchasePrice,
};

export const suppliersService = {
  /**
   * List suppliers with pagination and filters
   */
  async list(params: QuerySuppliersDto): Promise<SuppliersListResponse> {
    const response = await apiClient.get<ApiSuccess<SuppliersListResponse>>('/suppliers', { params });
    return response.data.data;
  },

  /**
   * Get single supplier by ID
   */
  async getById(id: string): Promise<SupplierDetailResponse> {
    const response = await apiClient.get<ApiSuccess<SupplierDetailResponse>>(`/suppliers/${id}`);
    return response.data.data;
  },

  /**
   * Create a new supplier
   */
  async create(data: CreateSupplierDto): Promise<SupplierCreateResponse> {
    const response = await apiClient.post<ApiSuccess<SupplierCreateResponse>>('/suppliers', data);
    return response.data.data;
  },

  /**
   * Update a supplier
   */
  async update(id: string, data: UpdateSupplierDto): Promise<SupplierUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<SupplierUpdateResponse>>(`/suppliers/${id}`, data);
    return response.data.data;
  },

  /**
   * Deactivate a supplier (soft delete)
   */
  async deactivate(id: string): Promise<void> {
    await apiClient.delete(`/suppliers/${id}`);
  },

  /**
   * Activate a supplier
   */
  async activate(id: string): Promise<void> {
    await apiClient.post(`/suppliers/${id}/activate`);
  },

  /**
   * Get suppliers KPIs
   */
  async getKPIs(): Promise<SupplierKPIsResponse> {
    const response = await apiClient.get<ApiSuccess<SupplierKPIsResponse>>('/suppliers/kpis');
    return response.data.data;
  },

  /**
   * Get active suppliers for dropdown
   */
  async getActive(): Promise<{ id: string; name: string }[]> {
    const response = await apiClient.get<ApiSuccess<{ id: string; name: string }[]>>('/suppliers/active');
    return response.data.data;
  },
};

export const catalogService = {
  /**
   * List catalog items for a supplier
   */
  async list(supplierId: string, params: QueryCatalogItemsDto): Promise<CatalogItemsListResponse> {
    const response = await apiClient.get<ApiSuccess<CatalogItemsListResponse>>(
      `/suppliers/${supplierId}/catalog`,
      { params }
    );
    return response.data.data;
  },

  /**
   * Get single catalog item
   */
  async getById(supplierId: string, itemId: string): Promise<CatalogItemDetailResponse> {
    const response = await apiClient.get<ApiSuccess<CatalogItemDetailResponse>>(
      `/suppliers/${supplierId}/catalog/${itemId}`
    );
    return response.data.data;
  },

  /**
   * Create a new catalog item
   */
  async create(supplierId: string, data: CreateCatalogItemDto): Promise<CatalogItemCreateResponse> {
    const response = await apiClient.post<ApiSuccess<CatalogItemCreateResponse>>(
      `/suppliers/${supplierId}/catalog`,
      data
    );
    return response.data.data;
  },

  /**
   * Update a catalog item
   */
  async update(
    supplierId: string,
    itemId: string,
    data: UpdateCatalogItemDto
  ): Promise<CatalogItemUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<CatalogItemUpdateResponse>>(
      `/suppliers/${supplierId}/catalog/${itemId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Update catalog item price
   */
  async updatePrice(
    supplierId: string,
    itemId: string,
    data: UpdateCatalogItemPriceDto
  ): Promise<CatalogItemUpdateResponse> {
    const response = await apiClient.put<ApiSuccess<CatalogItemUpdateResponse>>(
      `/suppliers/${supplierId}/catalog/${itemId}/price`,
      data
    );
    return response.data.data;
  },

  /**
   * Get price history for a catalog item
   */
  async getPriceHistory(supplierId: string, itemId: string): Promise<PriceHistoryResponse> {
    const response = await apiClient.get<ApiSuccess<PriceHistoryResponse>>(
      `/suppliers/${supplierId}/catalog/${itemId}/price-history`
    );
    return response.data.data;
  },

  /**
   * Deactivate a catalog item (soft delete)
   */
  async deactivate(supplierId: string, itemId: string): Promise<void> {
    await apiClient.delete(`/suppliers/${supplierId}/catalog/${itemId}`);
  },

  /**
   * Get purchase prices for a raw product (from all suppliers)
   */
  async getPurchasePricesForProduct(rawProductId: string): Promise<RawProductPurchasePricesResponse> {
    const response = await apiClient.get<ApiSuccess<RawProductPurchasePricesResponse>>(
      `/raw-products/${rawProductId}/purchase-prices`
    );
    return response.data.data;
  },
};
